import { CheckCircle, ExternalLink, Star } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router'
import api from 'services/api'
import { useBrand } from 'hooks/useBrand'
import { useInstantConsultation } from 'hooks/useInstantConsultation'

const STARS = [1, 2, 3, 4, 5]

export function ConclusionStep() {
  const { session, callDuration, resetFlow } = useInstantConsultation()
  const { brand } = useBrand()
  // Whitelabel: sem "minhas consultas" (página Cannect) e "Nova consulta" como
  // botão sólido — o theme css pinta bg-primary-*/hover de laranja cheio, o que
  // deixaria o outline com texto invisível.
  const isWhitelabel = brand !== 'cannect'
  const history = useHistory()
  const [rating, setRating] = useState(0)
  const [hovered, setHovered] = useState(0)
  const [rated, setRated] = useState(false)
  const [submittingRating, setSubmittingRating] = useState(false)

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return 'N/D'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    if (m === 0) return `${s}s`
    return `${m}min ${s}s`
  }

  const handleSubmitRating = useCallback(async () => {
    if (!rating || !session?.id) return
    setSubmittingRating(true)
    try {
      await api.post(`/session/rate/${session.id}`, { rating })
      setRated(true)
      toast.success('Avaliação enviada! Obrigado.')
    } catch {
      toast.error('Erro ao enviar avaliação.')
    } finally {
      setSubmittingRating(false)
    }
  }, [rating, session?.id])

  const handleNewConsultation = () => {
    resetFlow()
  }

  const handleGoToMyQueries = () => {
    history.push('/minhas-consultas')
  }

  return (
    <div className="mx-auto w-full max-w-lg px-4 py-10">
      {/* Ícone de sucesso */}
      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h1 className="font-spartan text-2xl font-bold text-gray-800">Consulta encerrada</h1>
        <p className="text-sm text-gray-500">Sua consulta foi concluída com sucesso. Esperamos ter ajudado!</p>
      </div>

      {/* Resumo da consulta */}
      <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold text-gray-600">Resumo da consulta</h3>
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Protocolo</span>
            <span className="font-mono font-medium text-gray-700">#{session?.id || '-'}</span>
          </div>

          {session?.prescriber && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Médico</span>
              <span className="font-medium text-gray-700">{session.prescriber.professional_name}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-gray-500">Duração</span>
            <span className="font-medium text-gray-700">{formatDuration(callDuration)}</span>
          </div>

          {session?.pathologies && session.pathologies.length > 0 && (
            <div className="flex items-start justify-between gap-4">
              <span className="text-gray-500">Condições tratadas</span>
              <span className="text-right font-medium text-gray-700">
                {session.pathologies.map((p) => p.pathology.name).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Receita */}
      {(session as any)?.recipe_link && (
        <a
          href={(session as any).recipe_link}
          target="_blank"
          rel="noopener noreferrer"
          className={`mb-4 flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-medium ${
            isWhitelabel
              ? 'border border-solid border-gray-200 bg-white text-primary-700 shadow-sm hover:border-gray-400'
              : 'border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
          }`}>
          <ExternalLink size={16} />
          Acessar receita médica
        </a>
      )}

      {/* Avaliação */}
      <div className="mb-6 rounded-2xl bg-white p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-gray-700">Como foi a sua consulta?</h3>
        {!rated ? (
          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-2">
              {STARS.map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHovered(star)}
                  onMouseLeave={() => setHovered(0)}
                  className="transition-transform hover:scale-110">
                  <Star
                    size={32}
                    className={`transition-colors ${
                      star <= (hovered || rating) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'
                    }`}
                  />
                </button>
              ))}
            </div>

            {rating > 0 && (
              <button
                type="button"
                onClick={handleSubmitRating}
                disabled={submittingRating}
                className="rounded-lg bg-primary-700 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-800 disabled:opacity-60">
                {submittingRating ? 'Enviando...' : 'Enviar avaliação'}
              </button>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-green-600">Avaliação enviada. Obrigado pelo feedback!</p>
        )}
      </div>

      {/* Ações */}
      <div className="flex flex-col gap-3">
        {!isWhitelabel && (
          <button
            type="button"
            onClick={handleGoToMyQueries}
            className="w-full rounded-lg bg-primary-700 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-800">
            Ver minhas consultas
          </button>
        )}

        <button
          type="button"
          onClick={handleNewConsultation}
          className={`w-full rounded-lg py-3.5 text-base transition-colors ${
            isWhitelabel
              ? 'border-none bg-primary-700 font-semibold text-white shadow-sm hover:bg-primary-800'
              : 'border border-primary-300 font-medium text-primary-700 hover:bg-primary-50'
          }`}>
          Nova consulta
        </button>
      </div>
    </div>
  )
}
